import { DISCS_PER_FORMAT, type Copy, type DiscFormat, type Movie } from './types';

/**
 * Does the shelf hold this movie in the given resolution at all?
 *
 * A combo case contains both discs, so it answers yes to `4k` *and* to `hd`.
 * This is the "can I watch it in 4K tonight" question — not the one the format
 * filter asks. See `formatBucket`.
 */
export function movieHasFormat(movie: Movie, format: DiscFormat): boolean {
  return movie.copies.some((copy) => copyHasFormat(copy, format));
}

export function copyHasFormat(copy: Copy, format: DiscFormat): boolean {
  if (copy.format === format) return true;
  return copy.format === 'combo' && (format === '4k' || format === 'hd');
}

/**
 * Which single bucket this movie belongs to, across everything on the shelf.
 *
 * The three buckets partition the collection — every movie is in exactly one:
 *
 * - `4k`    owned in UHD and not in HD
 * - `hd`    owned in HD and not in UHD
 * - `combo` owned in both
 *
 * "Both" covers a combo case and also a separate UHD case plus a separate
 * Blu-ray case: what matters is what you end up owning, not how many boxes it
 * came in. Null when there are no copies at all.
 */
export function formatBucket(movie: Movie): DiscFormat | null {
  let has4k = false;
  let hasHd = false;

  for (const copy of movie.copies) {
    if (copy.format === '4k' || copy.format === 'combo') has4k = true;
    if (copy.format === 'hd' || copy.format === 'combo') hasHd = true;
  }

  if (has4k && hasHd) return 'combo';
  if (has4k) return '4k';
  if (hasHd) return 'hd';
  return null;
}

/**
 * The colour of the spine on a poster tile.
 *
 * Deliberately the same answer as the format filter: a tile shown under
 * "Full HD" must not carry an amber spine.
 */
export function bestFormat(movie: Movie): DiscFormat | null {
  return formatBucket(movie);
}

/** Physical disc count, counting a combo case as two. */
export function discCount(movie: Movie): number {
  return movie.copies.reduce((total, copy) => total + DISCS_PER_FORMAT[copy.format], 0);
}

export function isLoanedOut(copy: Copy): boolean {
  return Boolean(copy.loan);
}

/** True when every copy is currently lent out — nothing to put in the player. */
export function isFullyLoanedOut(movie: Movie): boolean {
  return movie.copies.length > 0 && movie.copies.every(isLoanedOut);
}

export function loanedCopies(movie: Movie): Copy[] {
  return movie.copies.filter(isLoanedOut);
}
